/*
 * File: CustomStepper.tsx
 * Project: a-a
 * File Created: Monday, 23rd June 2025 4:25:10 pm
 * Author: Jackson Thounaojam (jackson.thounaojam@mutualmobile.com)
 * -----
 * Last Modified: Tuesday, 14th October 2025 12:00:49 am
 * Modified By: Jackson Thounaojam (jackson.thounaojam@mutualmobile.com)
 * -----
 * Copyright 2020 - 2025 Mutual Mobile, Mutual Mobile
 */
import * as React from 'react';
import Box from '@mui/material/Box';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepButton from '@mui/material/StepButton';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import zeplinColor from '../../../theme/Color';
import { StepIcon, SxProps } from '@mui/material';

export default function CustomStepper({
  activeStep,
  setActiveStep,
  setCompleted,
  steps = ['Deal details', 'Price book', 'Product'],
  completed,
  style,
}: {
  activeStep: number;
  steps?: string[];
  style?: React.CSSProperties | SxProps;
  setActiveStep: React.Dispatch<React.SetStateAction<number>>;
  setCompleted: React.Dispatch<
    React.SetStateAction<{
      [k: number]: boolean;
    }>
  >;
  completed: {
    [k: number]: boolean;
  };
}) {
  const totalSteps = () => {
    return steps.length;
  };

  const completedSteps = () => {
    return Object.keys(completed).length;
  };

  const isLastStep = () => {
    return activeStep === totalSteps() - 1;
  };

  const allStepsCompleted = () => {
    return completedSteps() === totalSteps();
  };

  const handleNext = () => {
    const newActiveStep =
      isLastStep() && !allStepsCompleted()
        ? // It's the last step, but not all steps have been completed,
          // find the first step that has been completed
          steps.findIndex((step, i) => !(i in completed))
        : activeStep + 1;
    setActiveStep(newActiveStep);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleStep = (step: number) => () => {
    setActiveStep(step);
  };

  const handleComplete = () => {
    setCompleted({
      ...completed,
      [activeStep]: true,
    });
    handleNext();
  };

  const handleReset = () => {
    setActiveStep(0);
    setCompleted({});
  };

  return (
    <Box sx={{ ...(style ? style : { width: '100%', padding: '24px 16px 10px 16px' }) }}>
      <Stepper
        // nonLinear
        activeStep={activeStep}
        sx={{ '& .MuiStepConnector-line': { borderColor: zeplinColor.Neutral5 } }}
        // alternativeLabel
      >
        {steps.map((label, index) => (
          <Step key={label} completed={completed[index]}>
            <StepButton
              sx={{
                '& .MuiSvgIcon-root': { width: '32px', height: '32px' },
                '& .MuiStepIcon-root.Mui-active': { color: zeplinColor.BasicBlack },
                '& .MuiStepIcon-root': {
                  color: index < activeStep ? zeplinColor.Success : zeplinColor.Neutral5,
                },
                '& .MuiStepLabel-label.Mui-active': {
                  fontWeight: 520,
                  color: zeplinColor.BasicBlack,
                  fontSize: '14px',
                },
                '& .MuiStepLabel-label': {
                  fontWeight: 520,
                  color: zeplinColor.Neutral8,
                  fontSize: '14px',
                },
              }}
              onClick={() => (index < activeStep ? setActiveStep(index) : undefined)}
            >
              {label}
            </StepButton>
          </Step>
        ))}
      </Stepper>
    </Box>
  );
}
